import { BarChartWrapper } from '@modules/charts/components/BarChartWrapper';
import { ChartTooltipWrapper } from '@modules/charts/components/ChartTooltipWrapper';
import { ChartTooltipWrapperProps } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipWrapper';
import {
  BarChartLabelWidthUnit,
  BarChartXAxisProps,
  BarChartYAxisProps,
} from '@modules/charts/types';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useGetColor,
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import {
  formatNumToCompactNotation,
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
} from '@modules/common/utils';
import { QuantifyCategoriesToggle } from '@modules/quantify/components/QuantifyCategoriesToggle';
import { QuantifyFormulaNameWithValue } from '@modules/quantify/components/QuantifyChart/QuantifyBarChart/QuantifyFormulaNameWithValue';
import { QuantifyReportStatusWidget } from '@modules/quantify/components/QuantifyReportStatusWidget';
import {
  useFetchQuantifyReport,
  useGetChartLabelOptions,
  useGetMeasuredAsDisplayName,
  useGetQuantifyChartData,
  useGetQuantifyFormulaDisplayName,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getQuantifyBarLabelWidthBuffer,
  isAggregateFormula,
} from '@modules/quantify/utils';
import { Stack, SxProps, Theme } from '@mui/material';
import pluralize from 'pluralize';
import { TimeRange } from 'v2/core/entities';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { QuantifyOrCompareChartTitle } from '../QuantifyOrCompareChartTitle';

export interface QuantifyBarChartProps<T> {
  shouldShowToggleGroup?: boolean;
  shouldShowAddToFilter?: boolean;
  shouldShowTitle?: boolean;
  shouldDisableInteraction?: boolean;
  yAxisCharLength?: number;
  xAxis?: Omit<BarChartXAxisProps<T>, 'dataKey' | 'getLabelWidthBuffer'>;
  yAxis?: Omit<BarChartYAxisProps<T>, 'dataKey'>;
  sxProps?: SxProps<Theme>;
  onResizeComplete?: (labelWidthPercentage: number) => void;
  defaultLabelWidth?: number;
  defaultLabelWidthUnits?: BarChartLabelWidthUnit;
  shouldTakeFullHeight?: boolean;
  minHeight?: number;
}

export function QuantifyBarChart<T>({
  shouldShowToggleGroup = true,
  shouldShowAddToFilter = true,
  shouldShowTitle: shouldShowTitleDefault = true,
  shouldDisableInteraction = false,
  yAxisCharLength,
  xAxis,
  yAxis,
  sxProps,
  onResizeComplete,
  defaultLabelWidth,
  defaultLabelWidthUnits,
  shouldTakeFullHeight = false,
  minHeight = 400,
}: QuantifyBarChartProps<T>) {
  const data = useGetQuantifyChartData();
  const duration = useQuantifyStore(({ query }) => query.duration);
  const formulaID = useQuantifyStore(({ query }) => query.formulaID);
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const whereClause = useQuantifyStore(
    ({ query }) => query.whereClauseConfig?.whereClause ?? EmptyWhereClause,
  );

  const getLabelOptions = useGetChartLabelOptions({
    data,
  });
  const fetchQuantifyReport = useFetchQuantifyReport();
  const isSingleRecordQuery = useIsSingleRecordQuery();

  const isDataEmpty = !data.length;

  const shouldShowTitle = shouldShowTitleDefault && !isDataEmpty;
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const { formulaDisplayName } = useGetQuantifyFormulaDisplayName();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const renderTooltipHeader: ChartTooltipWrapperProps['renderHeader'] =
    isAggregateFormula(formulaID) && isSingleRecordQuery
      ? (payload) => (
          <QuantifyFormulaNameWithValue value={payload?.value ?? 0} />
        )
      : undefined;

  const fieldName = groupBy ? getFieldNameFromEqlField(groupBy) : undefined;
  const qbField = groupBy ? getMetadataQBFieldFromEqlField(groupBy) : undefined;

  const displayName = groupBy
    ? getFormattedDisplayNameForEqlField({
        eqlField: groupBy,
        shouldRemoveFileUploadPrefix,
        isUnifiedFieldEnabled,
      })
    : undefined;
  const yAxisLabel = !!groupBy?.detail?.metadata
    ? getFormattedDisplayNameForEqlField({
        eqlField: groupBy,
        shouldRemoveFileUploadPrefix,
        isUnifiedFieldEnabled,
      })
    : pluralize(groupBy?.displayName || 'Record');

  const getChartColor = useGetColor({ fieldName });
  const measuredAsDisplayName = useGetMeasuredAsDisplayName({ formulaID });

  const formatBarValueLabel = (value: number): string => {
    return formulaID === EQLFunctionName.NormaliseByPercent
      ? `${value}%`
      : String(formatNumToCompactNotation({ value }));
  };

  return (
    <Stack height="100%">
      <Stack
        direction="row"
        justifyContent={shouldShowTitle ? 'space-between' : 'flex-end'}
        alignItems="center"
        my={!shouldShowTitle && !shouldShowToggleGroup ? 0 : 2}
      >
        {shouldShowTitle && (
          <QuantifyOrCompareChartTitle
            data={data}
            pl={3}
            getLabelOptions={getLabelOptions}
          />
        )}
        {shouldShowToggleGroup && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={3}
          >
            <QuantifyCategoriesToggle />
          </Stack>
        )}
      </Stack>
      {!isDataEmpty ? (
        <BarChartWrapper
          data={data}
          xAxis={{
            dataKey: 'value',
            label: measuredAsDisplayName,
            getLabelWidthBuffer: (dataPoint) =>
              getQuantifyBarLabelWidthBuffer({
                dataPoint,
                data,
              }),
            ...xAxis,
          }}
          yAxis={{
            dataKey: 'label',
            label: yAxisLabel,
            charLength: yAxisCharLength,
            ...yAxis,
          }}
          minHeight={minHeight}
          shouldTakeFullHeight={shouldTakeFullHeight}
          shouldDisableInteraction={shouldDisableInteraction}
          getLabelOptions={getLabelOptions}
          getChartColor={getChartColor}
          sxProps={sxProps}
          defaultLabelWidth={defaultLabelWidth}
          defaultLabelWidthUnits={defaultLabelWidthUnits}
          onResizeComplete={onResizeComplete}
          formatBarValueLabel={formatBarValueLabel}
          renderTooltipContent={({
            showTooltip,
            dataIndex,
            tooltipPosition,
            setShowTooltip,
            setTooltipPosition,
          }) => {
            return (
              <ChartTooltipWrapper
                showTooltip={showTooltip}
                payload={data[dataIndex ?? 0]}
                tooltipPosition={tooltipPosition}
                setShowTooltip={setShowTooltip}
                setTooltipPosition={setTooltipPosition}
                baseWhereClause={whereClause}
                fieldName={fieldName}
                qbField={qbField}
                displayName={displayName}
                dateRange={duration as TimeRange}
                shouldShowAddToFilter={shouldShowAddToFilter}
                getLabelOptions={getLabelOptions}
                onMergeComplete={fetchQuantifyReport}
                onFeedbackListDrawerClose={fetchQuantifyReport}
                renderHeader={renderTooltipHeader}
                shouldExcludeDataPointWhereClause={isSingleRecordQuery}
                formulaID={formulaID}
                measuredAsCriteria={measuredAsCriteria}
                isSingleRecordQuery={isSingleRecordQuery}
                formulaDisplayName={formulaDisplayName}
              />
            );
          }}
        />
      ) : (
        <QuantifyReportStatusWidget />
      )}
    </Stack>
  );
}
